import React, { useState, useEffect } from 'react';
import { Form, Button, Modal, Table, InputGroup } from 'react-bootstrap';
import axios from 'axios';

function JobManagement() {
    const [jobs, setJobs] = useState([]);
    const [title, setTitle] = useState('');
    const [desc, setDesc] = useState('');
    const [jobLocation, setJobLocation] = useState([]);
    const [endDate, setEndDate] = useState('');
    const [search, setSearch] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingJob, setEditingJob] = useState(null);

    useEffect(() => {
        fetchJobs();
    }, []);

    const fetchJobs = async () => {
        try {
            const response = await axios.get('https://api.biznetusa.com/api/get-jobs');
            setJobs(response.data.jobs);
        } catch (error) {
            console.error('Error fetching jobs:', error);
        }
    };

    const handleSaveJob = async () => {
        const payload = {
            title,
            desc,
            job_location: jobLocation.map(location => ({ location_id: location.location_id || location })), // Adjust location structure
            end_date: endDate
        };
        
        try {
            if (editingJob) {
                await axios.put(`https://api.biznetusa.com/api/update-job/${editingJob.id}`, payload);
            } else {
                await axios.post('https://api.biznetusa.com/api/store-job', payload);
            }
            fetchJobs();
            handleCloseModal();
        } catch (error) {
            console.error('Error saving job:', error);
        }
    };

    const handleDeleteJob = async (id) => {
        try {
            await axios.delete(`https://api.biznetusa.com/api/deletejob/${id}`);
            fetchJobs();
        } catch (error) {
            console.error('Error deleting job:', error);
        }
    };

    const handleShowModal = (job = null) => {
        if (job) {
            setEditingJob(job);
            setTitle(job.title);
            setDesc(job.description);
            setJobLocation(job.job_location); // Adjusted to include location object structure
            setEndDate(job.end_date);
        } else {
            setEditingJob(null);
            setTitle('');
            setDesc('');
            setJobLocation([]);
            setEndDate('');
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingJob(null);
    };

    const addJobLocation = () => {
        setJobLocation([...jobLocation, { location_id: '' }]);
    };

    const updateJobLocation = (index, value) => {
        const newJobLocation = [...jobLocation];
        newJobLocation[index].location_id = value; // Update location_id specifically
        setJobLocation(newJobLocation);
    };

    const removeJobLocation = (index) => {
        const newJobLocation = [...jobLocation];
        newJobLocation.splice(index, 1);
        setJobLocation(newJobLocation);
    };

    const filteredJobs = jobs.filter(job => job.title.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="container mt-4">
            <h2 className="mb-4 text-center">Job Management</h2>

            {/* Search Filter */}
            <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-light rounded">
                <h4>Jobs</h4>
                <InputGroup className="w-50">
                    <Form.Control
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by job title..."
                        className="shadow-sm"
                    />
                    <Button variant="primary" onClick={() => handleShowModal()} className="ms-2">
                        Add Job
                    </Button>
                </InputGroup>
            </div>

            <Table striped bordered hover className="shadow-sm">
                <thead className="bg-primary text-white">
                    <tr>
                        <th>Title</th>
                        <th>Description</th>
                        <th>Job Location</th>
                        <th>End Date</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredJobs.map((job) => (
                        <tr key={job.id}>
                            <td>{job.title}</td>
                            <td>{job.description}</td>
                            <td>{job.job_location.map(location => location.location_id).join(', ')}</td>
                            <td>{job.end_date}</td>
                            <td>
                                <Button variant="info" onClick={() => handleShowModal(job)} className="me-2">
                                    Edit
                                </Button>
                                <Button variant="danger" onClick={() => handleDeleteJob(job.id)}>
                                    Delete
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingJob ? 'Edit Job' : 'Add Job'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group controlId="title" className="mb-3">
                            <Form.Label>Job Title</Form.Label>
                            <Form.Control
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Enter job title"
                                className="shadow-sm"
                            />
                        </Form.Group>

                        <Form.Group controlId="desc" className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={desc}
                                onChange={(e) => setDesc(e.target.value)}
                                placeholder="Enter job description"
                                className="shadow-sm"
                            />
                        </Form.Group>

                        <Form.Group controlId="jobLocation" className="mb-3">
                            <Form.Label>Job Locations</Form.Label>
                            {jobLocation.map((location, index) => (
                                <div key={index} className="d-flex align-items-center mb-2">
                                    <Form.Control
                                        type="text"
                                        value={location.location_id}
                                        onChange={(e) => updateJobLocation(index, e.target.value)}
                                        placeholder="Enter job location"
                                        className="me-2 shadow-sm"
                                    />
                                    <Button
                                        variant="outline-danger"
                                        onClick={() => removeJobLocation(index)}
                                        className="btn-sm"
                                    >
                                        Remove
                                    </Button>
                                </div>
                            ))}
                            <Button variant="outline-secondary" onClick={addJobLocation} className="mt-2">
                                Add Location
                            </Button>
                        </Form.Group>

                        <Form.Group controlId="endDate" className="mb-3">
                            <Form.Label>End Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="shadow-sm"
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleSaveJob}>
                        {editingJob ? 'Save Changes' : 'Add Job'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default JobManagement;
